import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CButton, CDropdownItem, CFormInput, CFormSelect, CFormTextarea } from "@coreui/react";
import type { ApiWithdrawAddress } from "@tradingdroid/frontends-types";
import { CSpinnerText } from "@tradingdroid/frontends-components";

import { useRunDetail } from "@src/contexts/RunDetailContext";
import { useConfirmModal } from "@src/hooks/useConfirmModal";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import { COMMAND_POLL_MAX_ATTEMPTS, EXECUTION_POLL_DELAY_MS } from "@src/utils/constants";
import { delay } from "@src/utils/dateUtils";

interface Props {
  executionId: number;
  quoteCurrency: string | null;
  disabled: boolean;
  onStatusChange: (status: string | null) => void;
}

export const WithdrawCommandForm: React.FC<Props> = ({
  executionId,
  quoteCurrency,
  disabled,
  onStatusChange,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [addresses, setAddresses] = useState<ApiWithdrawAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressId, setAddressId] = useState<string>("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [pollingId, setPollingId] = useState<number | null>(null);
  const pollAbortRef = useRef(false);

  const { showToast } = useToastActions();
  const { confirmModal } = useConfirmModal();
  const { run, updateRun } = useRunDetail();
  const execution = run && "capital_target" in run ? run : null;
  const executionMetrics = execution?.live_metrics ?? execution?.metrics;
  const currentStrategyCapital =
    executionMetrics?.ending_capital ??
    executionMetrics?.final_capital ??
    executionMetrics?.invested_capital ??
    null;

  useEffect(() => {
    return () => {
      pollAbortRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (loading) onStatusChange("Submitting...");
    else if (pollingId != null) onStatusChange("Withdrawing...");
    else onStatusChange(null);
  }, [loading, pollingId, onStatusChange]);

  const loadAddresses = useCallback(async () => {
    setAddressesLoading(true);
    try {
      const res = await tdApi.getWithdrawAddresses();
      setAddresses(res?.items ?? []);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      showToast({
        type: "danger",
        title: "Failed to load addresses",
        message,
        timeout: 8000,
      });
    } finally {
      setAddressesLoading(false);
    }
  }, [showToast]);

  // `disabled` on CDropdownItem is only a stylesheet rule, so the handler refuses on its own.
  const handleClick = () => {
    if (disabled) return;
    const next = !showForm;
    setShowForm(next);
    if (next && addresses.length === 0 && !addressesLoading) {
      void loadAddresses();
    }
  };

  const pollWithdrawRequest = useCallback(
    async (requestId: number) => {
      setPollingId(requestId);
      pollAbortRef.current = false;
      let reachedTerminalState = false;

      for (let attempt = 0; attempt < COMMAND_POLL_MAX_ATTEMPTS; attempt++) {
        if (pollAbortRef.current) break;
        await delay(EXECUTION_POLL_DELAY_MS);

        try {
          const req = await tdApi.getWithdrawRequest(requestId);
          if (pollAbortRef.current) break;
          if (!req) continue;

          if (req.status === "completed") {
            reachedTerminalState = true;

            try {
              const refreshed = await tdApi.getExecution(executionId, {
                embed: "positions(movements(order)),live_metrics",
              });
              if (refreshed) updateRun(refreshed);
            } catch (fetchErr) {
              console.error("[WithdrawCommandForm] Failed to refresh execution:", fetchErr);
            }

            showToast({
              type: "success",
              title: "Withdrawal completed",
              message: `Sent ${req.amount} ${req.currency} on ${req.network_code} to ${req.address.slice(0, 6)}…${req.address.slice(-4)}.`,
              timeout: 12000,
            });
            break;
          } else if (req.status === "failed") {
            reachedTerminalState = true;
            const where = req.failed_in_state ? ` (at ${req.failed_in_state})` : "";
            showToast({
              type: "danger",
              title: "Withdrawal failed",
              message: `${req.error_message || "Unknown error"}${where}`,
              timeout: 15000,
            });
            break;
          } else if (req.status === "cancelled") {
            reachedTerminalState = true;
            showToast({
              type: "warning",
              title: "Withdrawal cancelled",
              message: `Request #${req.id} was cancelled.`,
              timeout: 8000,
            });
            break;
          }
        } catch (err) {
          console.error("[WithdrawCommandForm] Withdraw polling error:", err);
        }
      }

      if (!reachedTerminalState && !pollAbortRef.current) {
        showToast({
          type: "warning",
          title: `Withdraw #${requestId} still processing`,
          message: "Check Withdrawals history for the final status.",
          timeout: 15000,
        });
      }

      if (!pollAbortRef.current) {
        setPollingId(null);
      }
    },
    [executionId, showToast, updateRun],
  );

  const handleSubmit = async () => {
    const addressIdNum = parseInt(addressId, 10);
    const amountValue = parseFloat(amount);

    if (!isFinite(addressIdNum) || addressIdNum <= 0) {
      showToast({
        type: "warning",
        title: "Pick an address",
        message: "Select a saved withdraw address.",
        timeout: 4000,
      });
      return;
    }
    if (!isFinite(amountValue) || amountValue <= 0) {
      showToast({
        type: "warning",
        title: "Invalid amount",
        message: "Enter a positive amount to withdraw.",
        timeout: 4000,
      });
      return;
    }

    const address = addresses.find((a) => a.id === addressIdNum);
    const currency = quoteCurrency ?? "???";
    const destination = address
      ? `${address.label} (${address.network_code}) ${address.address.slice(0, 6)}…${address.address.slice(-4)}`
      : `address #${addressIdNum}`;

    const { confirmed } = await confirmModal(
      `Withdraw ${amountValue.toFixed(2)} ${currency} to ${destination}?${note ? `\n\nNote: ${note}` : ""}\n\nThis debits the execution capital and sends on-chain. Cannot be undone once on-chain.`,
      { title: "Confirm Withdrawal" },
    );
    if (!confirmed) return;

    setLoading(true);
    try {
      const req = await tdApi.createWithdrawRequest({
        execution_id: executionId,
        withdraw_address_id: addressIdNum,
        amount: amountValue,
        ...(note && { note }),
      });

      if (!req?.id) throw new Error("Withdraw request created but no ID returned");

      showToast({
        type: "info",
        title: `Withdraw #${req.id} submitted`,
        message: `${amountValue.toFixed(2)} ${currency} queued — waiting for worker...`,
        timeout: 5000,
      });

      setAmount("");
      setNote("");
      setAddressId("");
      setShowForm(false);

      pollWithdrawRequest(req.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      showToast({
        type: "danger",
        title: "Withdraw Failed",
        message: `Failed to submit withdrawal: ${message}`,
        timeout: 8000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CDropdownItem className="cursor-pointer" onClick={handleClick} disabled={disabled}>
        Withdraw {showForm ? "▴" : "▾"}
      </CDropdownItem>
      {showForm && (
        <div className="withdrawCommandForm px-3 py-2">
          {addressesLoading ? (
            <div className="withdrawCommandForm-empty">
              <small>Loading addresses...</small>
            </div>
          ) : addresses.length === 0 ? (
            <div className="withdrawCommandForm-empty">
              <small>
                No saved addresses. <Link to="/settings">Add one in User Settings →</Link>
              </small>
            </div>
          ) : (
            <>
              {currentStrategyCapital != null && (
                <div className="withdrawCommandForm-current mb-2">
                  <small className="text-body-secondary">
                    Available:
                    <br />
                    {currentStrategyCapital.toFixed(2)} {quoteCurrency ?? ""}
                  </small>
                </div>
              )}
              <div className="withdrawCommandForm-field mb-2">
                <small className="withdrawCommandForm-label">Address</small>
                <CFormSelect
                  size="sm"
                  value={addressId}
                  onChange={(e) => setAddressId(e.target.value)}
                >
                  <option value="">Select address...</option>
                  {addresses.map((addr) => (
                    <option key={addr.id} value={String(addr.id)}>
                      {addr.label} — {addr.network_code} ({addr.address.slice(0, 6)}…
                      {addr.address.slice(-4)})
                    </option>
                  ))}
                </CFormSelect>
              </div>
              <div className="withdrawCommandForm-field mb-2">
                <small className="withdrawCommandForm-label">
                  Amount ({quoteCurrency ?? "..."})
                </small>
                <CFormInput
                  type="number"
                  size="sm"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  min={0}
                  step={0.01}
                />
              </div>
              <div className="withdrawCommandForm-field mb-2">
                <small className="withdrawCommandForm-label">Note (optional)</small>
                <CFormTextarea
                  placeholder="optional note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  maxLength={500}
                />
              </div>
              <CButton
                color="primary"
                size="sm"
                className="w-100"
                onClick={handleSubmit}
                disabled={disabled || !addressId || !amount}
              >
                {loading ? <CSpinnerText text="Submitting..." /> : "Withdraw"}
              </CButton>
            </>
          )}
        </div>
      )}
    </>
  );
};
